import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Select from 'react-select';
import { DeleteSquare } from 'jbc-front/components/icons';

import { display } from 'src/utils/Utils';
import { formatInt, isValidNumericInput } from '../utils/declarationUtils';

const FixedMenuSelect = props => {
  const ref = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  return (
    <div ref={ref}>
      <Select
        {...props}
        menuContainerStyle={menuStyle || {}}
        onOpen={() => {
          if (!ref.current) return;
          const rect = ref.current.getBoundingClientRect();
          setMenuStyle({ position: 'fixed', top: rect.bottom, left: rect.left, width: rect.width, zIndex: 9999 });
        }}
        onClose={() => setMenuStyle(null)}
      />
    </div>
  );
};

const AllocationRow = ({
  allocation,
  index,
  isEditing,
  availableBranches,
  provinceOptions,
  getSubdistrictOptions,
  getRegionalTaxOfficeOptions,
  getTaxOfficeOptions,
  handleProvinceChange,
  handleSubdistrictChange,
  handleRegionalTaxOfficeChange,
  handleTaxOfficeChange,
  onDraftChange,
  onBatchUpdate,
  onDelete,
  errorFields
}) => {
  const { t } = useTranslation();

  const allocKey = allocation.id || allocation.tempId;
  const draftAlloc = isEditing ? allocation : null;
  const isHeadOffice = allocation.branchType === '01' || allocation.branchType === 'head_office';
  const isEmptyRow = allocation.isEmpty && !allocation.branchName;
  const currentProvinceId = (draftAlloc && draftAlloc.provinceId) || allocation.provinceId;
  const currentRegionalCode = (draftAlloc && draftAlloc.regionalTaxOfficeCode) || allocation.regionalTaxOfficeCode;

  const getBranchTypeLabel = branchType => {
    if (branchType === '01' || branchType === 'head_office') {
      return t('features.ReportIncomeTax.show.appendix.branch_type_head_office');
    }
    return t('features.ReportIncomeTax.show.appendix.branch_type_dependent');
  };

  const handleChange = field => event => {
    if (!onDraftChange) return;
    const value = event.target.value;
    if (!isValidNumericInput(value)) return;
    onDraftChange(allocKey, field, value);
  };

  const handleBranchSelect = value => {
    if (!value) return;
    const branch = availableBranches.find(b => b.id === Number(value));
    if (branch && onBatchUpdate) {
      onBatchUpdate(allocKey, {
        isEmpty: false,
        clientBranchId: branch.id,
        branchName: branch.name,
        provinceId: branch.provinceId ? String(branch.provinceId) : '',
        provinceName: branch.provinceName,
        districtId: branch.subdistrictId ? String(branch.subdistrictId) : '',
        districtName: branch.districtName,
        taxOfficeCode: branch.taxOfficeCode || '',
        taxOfficeName: branch.taxOfficeName,
        regionalTaxOfficeCode: branch.regionalTaxOfficeCode || '',
        parentTaxOfficeName: branch.regionalTaxOfficeName,
        taxCodeLocation: branch.corporateNumber
      });
    }
  };

  const renderAmount = value => formatInt(value);

  const renderEditable = (field, value) => (
    <input
      className={`declaration-input declaration-input--small${errorFields && errorFields.has(field) ? ' declaration-input--error' : ''}`}
      value={value != null ? value : ''}
      onChange={handleChange(field)}
      inputMode="numeric"
    />
  );

  const renderSelectField = (value, options, onChange, fieldName) => (
    <div className={errorFields && errorFields.has(fieldName) && !value ? 'declaration-select--error' : ''}>
      <FixedMenuSelect
        options={options}
        value={value || ''}
        onChange={onChange}
        placeholder={t('features.Clients.components.BasicInfoSectionShow.please_select')}
        noResultsText={t('features.ReportIncomeTax.show.appendix.no_data')}
        simpleValue
        clearable={false}
      />
    </div>
  );

  const renderValue = field => {
    if (!isEditing) return renderAmount(allocation[field]);
    return renderEditable(field, allocation[field]);
  };

  return (
    <tr>
      <td className="col-no">{index + 1}</td>
      <td>{getBranchTypeLabel(allocation.branchType)}</td>
      <td>
        {isEmptyRow ? (
          <div
            className={`declaration-select${
              errorFields && errorFields.has('clientBranchId') && !allocation.clientBranchId ? ' declaration-select--error' : ''
            }`}
          >
            <FixedMenuSelect
              options={availableBranches.map(branch => ({ value: branch.id, label: branch.name }))}
              onChange={handleBranchSelect}
              placeholder={t('features.ReportIncomeTax.show.appendix.select_branch')}
              simpleValue
              clearable={false}
              noResultsText={t('features.ReportIncomeTax.show.appendix.no_data')}
            />
          </div>
        ) : (
          allocation.branchName || '—'
        )}
      </td>
      <td>{allocation.taxCodeLocation || '—'}</td>
      <td>
        {isEditing && draftAlloc && !isEmptyRow
          ? renderSelectField(draftAlloc.provinceId, provinceOptions, handleProvinceChange(allocKey), 'provinceId')
          : display(allocation.provinceName)}
      </td>
      <td>
        {isEditing && draftAlloc && !isEmptyRow
          ? renderSelectField(
              draftAlloc.districtId,
              getSubdistrictOptions(currentProvinceId),
              handleSubdistrictChange(allocKey, currentProvinceId),
              'districtId'
            )
          : display(allocation.districtName)}
      </td>
      <td>
        {isEditing && draftAlloc && !isEmptyRow
          ? renderSelectField(
              draftAlloc.regionalTaxOfficeCode,
              getRegionalTaxOfficeOptions(currentProvinceId),
              handleRegionalTaxOfficeChange(allocKey),
              'regionalTaxOfficeCode'
            )
          : display(allocation.parentTaxOfficeName)}
      </td>
      <td>
        {isEditing && draftAlloc && !isEmptyRow
          ? renderSelectField(draftAlloc.taxOfficeCode, getTaxOfficeOptions(currentRegionalCode), handleTaxOfficeChange(allocKey), 'taxOfficeCode')
          : display(allocation.taxOfficeName)}
      </td>
      <td className="col-amount">{isEmptyRow ? '—' : renderValue('ct10TaxableIncome')}</td>
      <td className="col-amount">{isEmptyRow ? '—' : renderValue('ct11TaxableIncomeSubjectToWh')}</td>
      <td className="col-amount">{isEmptyRow ? '—' : renderValue('ct12EmployeeCount')}</td>
      <td className="col-amount">{isEmptyRow ? '—' : renderValue('ct13EmployeesWithTax')}</td>
      <td className="col-amount">{isEmptyRow ? '—' : renderValue('ct14TaxWithheld')}</td>
      {isEditing && (
        <td className="col-action">
          {!isHeadOffice && (
            <button type="button" className="delete-btn" onClick={() => onDelete(allocKey)}>
              <DeleteSquare size={18} />
            </button>
          )}
        </td>
      )}
    </tr>
  );
};

AllocationRow.propTypes = {
  allocation: PropTypes.shape({
    id: PropTypes.number,
    tempId: PropTypes.string,
    clientBranchId: PropTypes.number,
    branchType: PropTypes.string,
    branchName: PropTypes.string,
    provinceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    provinceName: PropTypes.string,
    districtId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    districtName: PropTypes.string,
    taxOfficeCode: PropTypes.string,
    taxOfficeName: PropTypes.string,
    parentTaxOfficeName: PropTypes.string,
    regionalTaxOfficeCode: PropTypes.string,
    taxCodeLocation: PropTypes.string,
    ct10TaxableIncome: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct11TaxableIncomeSubjectToWh: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct12EmployeeCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct13EmployeesWithTax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct14TaxWithheld: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    isEmpty: PropTypes.bool
  }).isRequired,
  index: PropTypes.number.isRequired,
  isEditing: PropTypes.bool.isRequired,
  availableBranches: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      provinceId: PropTypes.number,
      provinceName: PropTypes.string,
      subdistrictId: PropTypes.number,
      districtName: PropTypes.string,
      taxOfficeCode: PropTypes.string,
      taxOfficeName: PropTypes.string,
      regionalTaxOfficeCode: PropTypes.string,
      regionalTaxOfficeName: PropTypes.string,
      corporateNumber: PropTypes.string
    })
  ).isRequired,
  provinceOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })).isRequired,
  getSubdistrictOptions: PropTypes.func.isRequired,
  getRegionalTaxOfficeOptions: PropTypes.func.isRequired,
  getTaxOfficeOptions: PropTypes.func.isRequired,
  handleProvinceChange: PropTypes.func.isRequired,
  handleSubdistrictChange: PropTypes.func.isRequired,
  handleRegionalTaxOfficeChange: PropTypes.func.isRequired,
  handleTaxOfficeChange: PropTypes.func.isRequired,
  onDraftChange: PropTypes.func,
  onBatchUpdate: PropTypes.func,
  onDelete: PropTypes.func.isRequired,
  errorFields: PropTypes.instanceOf(Set)
};

AllocationRow.defaultProps = {
  onDraftChange: null,
  onBatchUpdate: null,
  errorFields: null
};

export default AllocationRow;
